import { Button } from "@/components/ui/button";
import { CardContent, CardFooter } from "@/components/ui/card";
import { FormFieldWrapper } from "@/components/ui/form-field-wrapper";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { SuggestionList } from "@/components/ui/suggestion-list";
import { useAppForm } from "@/components/ui/tanstack-form";
import { UserType } from "@/constants";
import { updateLicenseMutation } from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  PropertyOwnerResponseSchema,
  RegistrantAccountResponseSchema,
} from "@/hera-api/client/types.gen";
import { usePlacesAutocomplete } from "@/hooks/use-places-autocomplete";
import { notifyLicenseDetailsChanged } from "@/lib/license-details-change-notification";
import { parseGoogleAddressComponents } from "@/lib/parse-google-address-components";
import {
  getRegistrantDisplayAddress1,
  getRegistrantDisplayAddress2,
  getRegistrantDisplayCity,
  getRegistrantDisplayCompanyName,
  getRegistrantDisplayName,
  getRegistrantDisplayState,
  getRegistrantDisplayZipcode,
  hasRegistrantDisplayAddress,
} from "@/lib/registrant-display";
import { formatPhoneNumber, getQueryErrorMessage } from "@/lib/utils";
import { useStore } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { renderQuestionField } from "../../registrant/form-utils/question-field-utils";
import { PersonSchema } from "../schemas/validation-schemas";

interface PersonCardContentProps {
  person: RegistrantAccountResponseSchema | PropertyOwnerResponseSchema | null;
  userType: UserType;
  licenseId: string;
  isPropertyOwner?: boolean;
  /** company_contact NAC tile: company first, person field labeled "Contact". */
  useContactLabel?: boolean;
}

interface EditablePropertyOwnerInfoProps {
  person: PropertyOwnerResponseSchema | null;
  licenseId: string;
  onSave: () => void;
  onReset: () => void;
}

function EditablePropertyOwnerInfo({
  person,
  licenseId,
  onSave,
  onReset,
}: EditablePropertyOwnerInfoProps) {
  const queryClient = useQueryClient();
  const firstInputRef = useRef<HTMLInputElement>(null);

  const updateLicense = useMutation({
    ...updateLicenseMutation(),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (firstInputRef.current) {
        firstInputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const form = useAppForm({
    defaultValues: {
      name: person?.name || "",
      email: person?.email || null,
      phone: person?.phone || null,
      address1: person?.address1 || null,
      address2: person?.address2 || null,
      city: person?.city || null,
      state: person?.state || null,
      zipcode: person?.zipcode || null,
    },
    validators: {
      onSubmit: PersonSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateLicense.mutateAsync({
          path: { license_id: licenseId },
          body: {
            property: {
              owner: value,
            },
          },
        });
        await notifyLicenseDetailsChanged(queryClient, licenseId);
        toast({
          title: "Success",
          description: "Property owner information updated successfully",
          variant: "secondary",
        });
        onSave();
      } catch (error) {
        toast({
          title: "Error",
          description: `${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
      }
    },
  });

  const address1Value = useStore(
    form.store,
    (state) => state.values.address1 ?? "",
  );
  const { suggestions: addressSuggestions } = usePlacesAutocomplete(
    address1Value,
    {
      includedRegionCodes: ["us"],
      includedPrimaryTypes: ["street_address"],
    },
  );

  const handleAddressSuggestionClick = async (
    suggestion: google.maps.places.AutocompleteSuggestion,
  ) => {
    const place = suggestion.placePrediction?.toPlace();
    if (!place) return;
    await place.fetchFields({ fields: ["addressComponents"] });
    if (!place?.addressComponents) return;
    const { address1, address2, city, state, zipcode } =
      parseGoogleAddressComponents(place.addressComponents);
    form.setFieldValue("address1", address1);
    form.validateField("address1", "change");
    form.setFieldValue("address2", address2);
    form.validateField("address2", "change");
    form.setFieldValue("city", city);
    form.validateField("city", "change");
    form.setFieldValue("state", state);
    form.validateField("state", "change");
    form.setFieldValue("zipcode", zipcode);
    form.validateField("zipcode", "change");
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
  };

  const handleReset = () => {
    form.reset();
    onReset();
  };

  return (
    <CardContent className="space-y-2">
      <form.AppForm>
        <form className="space-y-4" onSubmit={handleSave}>
          <div className="space-y-4">
            <form.AppField
              name="name"
              validators={{
                onChange: PersonSchema.shape.name,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Name">
                  {renderQuestionField("text", field, undefined, firstInputRef)}
                </FormFieldWrapper>
              )}
            </form.AppField>

            <form.AppField
              name="email"
              validators={{
                onChange: PersonSchema.shape.email,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Email">
                  <Input
                    type="email"
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                </FormFieldWrapper>
              )}
            </form.AppField>

            <form.AppField
              name="phone"
              validators={{
                onChange: PersonSchema.shape.phone,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Phone">
                  {renderQuestionField("text", field)}
                </FormFieldWrapper>
              )}
            </form.AppField>

            <form.AppField
              name="address1"
              validators={{
                onChange: PersonSchema.shape.address1,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Address">
                  <SuggestionList
                    closeOnBlur
                    suggestions={addressSuggestions}
                    renderSuggestion={(suggestion) => (
                      <span>{suggestion.placePrediction?.text?.text}</span>
                    )}
                    getSuggestionKey={(suggestion) =>
                      suggestion.placePrediction?.placeId ?? ""
                    }
                    onSuggestionSelect={handleAddressSuggestionClick}
                  >
                    <Input
                      value={field.state.value ?? ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="Enter street address"
                    />
                  </SuggestionList>
                </FormFieldWrapper>
              )}
            </form.AppField>

            <form.AppField
              name="address2"
              validators={{
                onChange: PersonSchema.shape.address2,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Address 2">
                  {renderQuestionField("text", field)}
                </FormFieldWrapper>
              )}
            </form.AppField>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <form.AppField
                name="city"
                validators={{
                  onChange: PersonSchema.shape.city,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="City">
                    {renderQuestionField("text", field)}
                  </FormFieldWrapper>
                )}
              </form.AppField>

              <form.AppField
                name="state"
                validators={{
                  onChange: PersonSchema.shape.state,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="State">
                    {renderQuestionField("text", field)}
                  </FormFieldWrapper>
                )}
              </form.AppField>

              <form.AppField
                name="zipcode"
                validators={{
                  onChange: PersonSchema.shape.zipcode,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Zipcode">
                    {renderQuestionField("text", field)}
                  </FormFieldWrapper>
                )}
              </form.AppField>
            </div>
          </div>

          <CardFooter className="flex justify-between px-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              disabled={updateLicense.isPending}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  type="submit"
                  disabled={!canSubmit || updateLicense.isPending}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSubmitting || updateLicense.isPending
                    ? "Saving..."
                    : "Save"}
                </Button>
              )}
            </form.Subscribe>
          </CardFooter>
        </form>
      </form.AppForm>
    </CardContent>
  );
}

interface ReadOnlyPersonInfoProps {
  person: RegistrantAccountResponseSchema | PropertyOwnerResponseSchema | null;
  userType: UserType;
  isPropertyOwner?: boolean;
  onEdit: () => void;
  useContactLabel?: boolean;
}

function ReadOnlyPersonInfo({
  person,
  userType,
  isPropertyOwner = false,
  onEdit,
  useContactLabel = false,
}: ReadOnlyPersonInfoProps) {
  const canEdit = userType === UserType.OPERATOR && isPropertyOwner;

  const nameLabel = useContactLabel ? "Contact" : "Name";
  const showName = Boolean(getRegistrantDisplayName(person));
  const showCompany = Boolean(getRegistrantDisplayCompanyName(person));
  const nameBlock =
    showName && person ? (
      <div>
        <div className="flex gap-4">
          <div className="flex-1">
            <p className="text-sm font-bold">{nameLabel}</p>
            <p className="text-sm">{getRegistrantDisplayName(person)}</p>
          </div>
        </div>
      </div>
    ) : null;
  const companyBlock =
    showCompany && person ? (
      <div>
        <p className="text-sm font-bold">Company name</p>
        <p className="text-sm">{getRegistrantDisplayCompanyName(person)}</p>
      </div>
    ) : null;

  const nameAndCompanyRow =
    nameBlock || companyBlock ? (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {useContactLabel ? (
          <>
            {companyBlock}
            {nameBlock}
          </>
        ) : (
          <>
            {nameBlock}
            {companyBlock}
          </>
        )}
      </div>
    ) : null;

  return (
    <CardContent className="space-y-2">
      {person ? (
        <>
          {nameAndCompanyRow}
          {(person.email || person.phone) && (
            <div>
              <div className="flex gap-4">
                {person.email && (
                  <div className="flex-1">
                    <p className="text-sm font-bold">Email</p>
                    <p className="text-sm">{person.email}</p>
                  </div>
                )}
                {person.phone && (
                  <div className="flex-1">
                    <p className="text-sm font-bold">Phone</p>
                    <p className="text-sm">{formatPhoneNumber(person.phone)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          {hasRegistrantDisplayAddress(person) && (
            <div>
              <p className="text-sm font-bold">Address</p>
              {getRegistrantDisplayAddress1(person) && (
                <p className="text-sm">
                  {getRegistrantDisplayAddress1(person)}
                </p>
              )}
              {getRegistrantDisplayAddress2(person) && (
                <p className="text-sm">
                  {getRegistrantDisplayAddress2(person)}
                </p>
              )}
              <p className="text-sm">
                {[
                  getRegistrantDisplayCity(person),
                  getRegistrantDisplayState(person),
                  getRegistrantDisplayZipcode(person),
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm">No information available</p>
      )}
      {canEdit && (
        <CardFooter className="flex justify-end px-0 pt-4">
          <Button variant="outline" onClick={onEdit}>
            Edit
          </Button>
        </CardFooter>
      )}
    </CardContent>
  );
}

export function PersonCardContent({
  person,
  userType,
  licenseId,
  isPropertyOwner = false,
  useContactLabel = false,
}: PersonCardContentProps) {
  const [isEditing, setIsEditing] = useState(false);

  if (userType === UserType.OPERATOR && isPropertyOwner && isEditing) {
    return (
      <EditablePropertyOwnerInfo
        person={person}
        licenseId={licenseId}
        onSave={() => setIsEditing(false)}
        onReset={() => setIsEditing(false)}
      />
    );
  }

  return (
    <ReadOnlyPersonInfo
      person={person}
      userType={userType}
      isPropertyOwner={isPropertyOwner}
      onEdit={() => setIsEditing(true)}
      useContactLabel={useContactLabel}
    />
  );
}
